/**
 * Il fascicolo annuale, pronto da stampare.
 *
 * Il PDF lo fa il browser: «Stampa → Salva come PDF». Non è una scorciatoia
 * per non scrivere codice, è la scelta giusta — il foglio che esce ha la
 * tipografia del sistema, si rigenera con i dati veri a ogni apertura, e non
 * aggiunge una libreria che poi qualcuno deve mantenere.
 *
 * Il documento segue una regola sola: **i mesi chiusi vengono dalla fotografia
 * congelata**. Stampato a gennaio e ristampato a marzo dice la stessa cosa. Se
 * nel frattempo il gestionale è cambiato, il fascicolo lo scrive in fondo
 * invece di cambiare la cifra in silenzio.
 */

import { useEffect, useState } from "react";
import { FileText, Printer } from "lucide-react";
import {
  formatEur,
  formatEurDelta,
  type Fascicolo as Dati,
  type MeseDelFascicolo,
} from "@otto/shared";
import { api } from "../lib/api.ts";
import { messaggioDi } from "../lib/errori.ts";
import { Card, Skeleton, cx } from "../ui/primitives.tsx";

const MESI = [
  "gennaio",
  "febbraio",
  "marzo",
  "aprile",
  "maggio",
  "giugno",
  "luglio",
  "agosto",
  "settembre",
  "ottobre",
  "novembre",
  "dicembre",
];

const CANALI: Record<string, string> = {
  airbnb: "Airbnb",
  booking: "Booking",
  vrbo: "Vrbo",
  diretto: "Diretto",
};

export function Fascicolo({
  anno,
  barra = true,
}: {
  anno: number;
  /** La riga con il bottone di stampa. Chi lo apre in finestra la mette sotto. */
  barra?: boolean;
}) {
  const [dati, setDati] = useState<Dati | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    setDati(null);
    setErrore(null);
    void api
      .fascicolo(anno)
      .then(({ fascicolo }) => setDati(fascicolo))
      .catch((e) => setErrore(messaggioDi(e)));
  }, [anno]);

  if (errore) return <p className="p-4 text-[14px] text-danger">{errore}</p>;

  if (!dati)
    return (
      <div className="flex flex-col gap-3 p-4">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );

  return <Foglio dati={dati} barra={barra} />;
}

/**
 * Il foglio, dato il contenuto.
 *
 * Separato da chi lo carica per una ragione pratica: così si può renderlo
 * fuori dal browser — per controllare come viene **stampato**, che è l'unica
 * verifica che conta su un documento destinato al PDF — e provarlo senza
 * mettere in mezzo un finto della rete.
 */
export function Foglio({
  dati,
  barra = true,
}: {
  dati: Dati;
  barra?: boolean;
}) {
  return (
    <div className="fascicolo mx-auto w-full max-w-[860px]">
      {/* Il bottone non finisce nel foglio: `no-print`. */}
      <div
        className={cx(
          "no-print mb-5 flex items-center justify-between gap-3",
          !barra && "hidden",
        )}
      >
        <p className="text-[13px] leading-relaxed text-ink-soft">
          Stampa e scegli «Salva come PDF». I mesi chiusi non cambiano più:
          quello che esce oggi esce uguale fra tre mesi.
        </p>
        <button
          type="button"
          onClick={() => window.print()}
          className="flex shrink-0 items-center gap-2 rounded-pill bg-accent px-4 py-2.5 text-[13.5px] font-bold text-ink-inverse transition-transform duration-quick active:scale-95"
        >
          <Printer size={16} />
          Stampa o salva in PDF
        </button>
      </div>

      <article className="foglio">
        <header className="mb-6 border-b border-line pb-4">
          <h1 className="text-[22px] font-extrabold tracking-tight text-ink">
            Affitti brevi {dati.anno} — riepilogo
          </h1>
          <p className="mt-1 text-[12.5px] text-ink-soft">
            Documento generato il{" "}
            {new Date(dati.generatoIl).toLocaleDateString("it-IT")}
            {dati.regimeFiscale ? ` · regime ${dati.regimeFiscale}` : ""}
          </p>
        </header>

        <NonFirmabile dati={dati} />

        {/* ── 1. Il totale dell'anno ─────────────────────────────────── */}
        <section className="mb-7">
          <h2 className="mb-2.5 text-[15px] font-bold text-ink">
            1. Totale dell&apos;anno
          </h2>
          <Riepilogo dati={dati} />
        </section>

        {/* ── 2. I dodici mesi, e una riga per casa ─────────────────── */}
        <section className="evita-taglio mb-7">
          <h2 className="mb-2.5 text-[15px] font-bold text-ink">
            2. I dodici mesi
          </h2>
          <Tabella>
            <thead>
              <tr>
                <Th>Mese</Th>
                <Th n>Notti</Th>
                <Th n>Incassato</Th>
                <Th n>Comm. a tuo carico</Th>
                <Th n>Imp. soggiorno</Th>
                <Th n>Altri costi</Th>
                <Th n>Utile</Th>
              </tr>
            </thead>
            <tbody>
              {mesiDelPortafoglio(dati).map((m) => (
                <tr key={m.mese}>
                  <Td>
                    {MESI[Number(m.mese.slice(5, 7)) - 1]}
                    {dati.mesiProvvisori.includes(m.mese) ? (
                      <span className="text-ink-faint"> (provvisorio)</span>
                    ) : null}
                    {m.discordante ? (
                      <span className="font-semibold text-danger">
                        {" "}
                        (da rifare)
                      </span>
                    ) : null}
                    {m.dichiarato ? (
                      <span className="text-ink-faint"> (inserito)</span>
                    ) : null}
                    {m.rettificato && !m.discordante && !m.dichiarato ? (
                      <span className="text-ink-faint"> (rettificato)</span>
                    ) : null}
                  </Td>
                  <Td n>{m.nottiVendute}</Td>
                  <Td n>
                    {formatEur(m.ricaviLordiCents)}
                    {m.discordante ? (
                      <div className="text-[11px] font-normal text-danger">
                        oggi {formatEur(m.ricaviOggiCents ?? 0)}
                      </div>
                    ) : null}
                  </Td>
                  <Td n>{formatEur(m.commissioniCents)}</Td>
                  <Td n>{formatEur(m.impostaSoggiornoCents)}</Td>
                  <Td n>{formatEur(m.altriCostiCents)}</Td>
                  <Td n>{formatEur(m.utileCasaCents)}</Td>
                </tr>
              ))}
              <tr className="font-bold">
                <Td>Totale</Td>
                <Td n>{dati.totali.nottiVendute}</Td>
                <Td n>{formatEur(dati.totali.ricaviLordiCents)}</Td>
                <Td n>{formatEur(dati.totali.commissioniCents)}</Td>
                <Td n>{formatEur(dati.totali.impostaSoggiornoCents)}</Td>
                <Td n>{formatEur(dati.totali.altriCostiCents)}</Td>
                <Td n>{formatEur(dati.totali.utileCasaCents)}</Td>
              </tr>
            </tbody>
          </Tabella>
        </section>

        {/* ── 3. Per canale: dove trattenuto e fatturato si separano ─── */}
        <section className="evita-taglio mb-7">
          <h2 className="mb-1 text-[15px] font-bold text-ink">3. Per canale</h2>
          <p className="mb-2.5 text-[12px] leading-relaxed text-ink-soft">
            «Incassato» è quello che il canale ha versato sul conto. Airbnb
            versa <strong>già al netto</strong> della sua commissione, Booking
            versa il lordo e la commissione la fattura a parte: le due cose
            stanno su due colonne diverse perché fiscalmente non sono la stessa.
          </p>
          <Tabella>
            <thead>
              <tr>
                <Th>Canale</Th>
                <Th n>Pren.</Th>
                <Th n>Venduto lordo</Th>
                <Th n>Incassato</Th>
                <Th n>Comm. trattenute</Th>
                <Th n>Comm. fatturate</Th>
                <Th n>Imp. sogg. a te</Th>
              </tr>
            </thead>
            <tbody>
              {dati.canali.map((c) => (
                <tr key={c.canale}>
                  <Td>{CANALI[c.canale] ?? c.canale}</Td>
                  <Td n>{c.prenotazioni}</Td>
                  <Td n>{formatEur(c.vendutoLordoCents)}</Td>
                  <Td n>{formatEur(c.incassatoCents)}</Td>
                  <Td n>{formatEur(c.commissioniTrattenuteCents)}</Td>
                  <Td n>{formatEur(c.commissioniFatturateCents)}</Td>
                  <Td n>{formatEur(c.impostaSoggiornoDaVersareCents)}</Td>
                </tr>
              ))}
              <tr className="font-bold">
                <Td>Totale</Td>
                <Td n>{somma(dati.canali, (c) => c.prenotazioni)}</Td>
                <Td n>
                  {formatEur(somma(dati.canali, (c) => c.vendutoLordoCents))}
                </Td>
                <Td n>
                  {formatEur(somma(dati.canali, (c) => c.incassatoCents))}
                </Td>
                <Td n>
                  {formatEur(
                    somma(dati.canali, (c) => c.commissioniTrattenuteCents),
                  )}
                </Td>
                <Td n>
                  {formatEur(
                    somma(dati.canali, (c) => c.commissioniFatturateCents),
                  )}
                </Td>
                <Td n>
                  {formatEur(
                    somma(dati.canali, (c) => c.impostaSoggiornoDaVersareCents),
                  )}
                </Td>
              </tr>
            </tbody>
          </Tabella>
        </section>

        {/* ── 4. Una riga per casa: con cento immobili è due pagine ──── */}
        <section className="evita-taglio mb-7">
          <h2 className="mb-2.5 text-[15px] font-bold text-ink">
            4. Per immobile
          </h2>
          <Tabella>
            <thead>
              <tr>
                <Th>Immobile</Th>
                <Th>CIN</Th>
                <Th n>Notti</Th>
                <Th n>Incassato</Th>
                <Th n>Comm. a tuo carico</Th>
                <Th n>Imp. soggiorno</Th>
                <Th n>Utile</Th>
              </tr>
            </thead>
            <tbody>
              {dati.immobili.map((i) => (
                <tr key={i.listingId}>
                  <Td>
                    {i.nickname}
                    {i.citta ? (
                      <span className="text-ink-faint"> · {i.citta}</span>
                    ) : null}
                  </Td>
                  <Td>
                    <span className="font-mono text-[11.5px]">
                      {i.cin ?? "—"}
                    </span>
                  </Td>
                  <Td n>{i.totali.nottiVendute}</Td>
                  <Td n>{formatEur(i.totali.ricaviLordiCents)}</Td>
                  <Td n>{formatEur(i.totali.commissioniCents)}</Td>
                  <Td n>{formatEur(i.totali.impostaSoggiornoCents)}</Td>
                  <Td n>{formatEur(i.totali.utileCasaCents)}</Td>
                </tr>
              ))}
              <tr className="font-bold">
                <Td>Totale</Td>
                <Td />
                <Td n>{dati.totali.nottiVendute}</Td>
                <Td n>{formatEur(dati.totali.ricaviLordiCents)}</Td>
                <Td n>{formatEur(dati.totali.commissioniCents)}</Td>
                <Td n>{formatEur(dati.totali.impostaSoggiornoCents)}</Td>
                <Td n>{formatEur(dati.totali.utileCasaCents)}</Td>
              </tr>
            </tbody>
          </Tabella>
        </section>

        {/* ── 5. Quello che il documento deve dichiarare ──────────────── */}
        <Note dati={dati} />
      </article>
    </div>
  );
}

/**
 * L'avvertimento che sta **prima** dei numeri.
 *
 * La discordanza fra la fotografia e quello che il gestionale dice oggi era
 * scritta in nota 4, in fondo, dopo una tabella che intanto stampava i numeri
 * vecchi come se fossero il conto dell'anno. Su questo account erano 21 mesi
 * su 24, e nessuno li avrebbe letti: un documento che si fa scoprire dal
 * commercialista invece che dall'autore. Quando i conti non tornano, la prima
 * cosa che si legge è che non tornano.
 */
function NonFirmabile({ dati }: { dati: Dati }) {
  if (dati.mesiCambiati.length === 0) return null;

  const mesi = [...new Set(dati.mesiCambiati.map((c) => c.mese))].sort();
  const differenza = dati.mesiCambiati.reduce(
    (somma, c) => somma + (c.oggiCents - c.primaCents),
    0,
  );

  return (
    <section className="evita-taglio mb-6 rounded-card border border-danger/30 bg-danger-soft px-4 py-3">
      <p className="text-[13px] font-bold text-danger">
        Questo documento non è ancora da consegnare.
      </p>
      <p className="mt-1 text-[12.5px] leading-relaxed text-ink">
        Su <strong>{mesi.length}</strong> {mesi.length === 1 ? "mese" : "mesi"}{" "}
        il conto chiuso a suo tempo non coincide con quello che il gestionale
        dice oggi: <strong>{formatEurDelta(differenza)}</strong> di incassato in{" "}
        {differenza >= 0 ? "più" : "meno"}. I mesi marcati «da rifare» nella
        tabella qui sotto vanno ricalcolati prima di firmare. Il dettaglio, casa
        per casa, è nelle note in fondo.
      </p>
    </section>
  );
}

/**
 * Le note in fondo.
 *
 * Non sono un dettaglio di cortesia: un documento contabile che tace su cosa
 * è stimato, cosa è provvisorio e cosa nel frattempo è cambiato sul gestionale
 * è un documento che si fa scoprire dal commercialista invece che dall'autore.
 */
function Note({ dati }: { dati: Dati }) {
  const stimato = dati.totali.stimatoCents;
  if (
    stimato === 0 &&
    dati.mesiProvvisori.length === 0 &&
    dati.mesiCambiati.length === 0 &&
    dati.mesiRettificati.length === 0
  )
    return null;

  return (
    <section className="evita-taglio border-t border-line pt-4">
      <h2 className="mb-2 text-[15px] font-bold text-ink">4. Note</h2>
      <ul className="flex flex-col gap-1.5 text-[12.5px] leading-relaxed text-ink-soft">
        {stimato > 0 ? (
          <li>
            <strong>{formatEur(stimato)}</strong> dei costi sono stime di Otto,
            non numeri confermati da una ricevuta.
          </li>
        ) : null}
        {dati.mesiProvvisori.length > 0 ? (
          <li>
            Mesi non ancora chiusi, quindi ancora modificabili:{" "}
            {dati.mesiProvvisori
              .map((m) => MESI[Number(m.slice(5, 7)) - 1])
              .join(", ")}
            .
          </li>
        ) : null}
        {dati.mesiDichiarati.length > 0 ? (
          <li>
            Mesi con i numeri inseriti a mano dall&apos;host:
            <ul className="mt-1 flex flex-col gap-0.5 pl-4">
              {dati.mesiDichiarati.map((d) => (
                <li key={`${d.nickname}-${d.mese}`}>
                  {d.nickname}, {MESI[Number(d.mese.slice(5, 7)) - 1]}: inseriti
                  il {new Date(d.dichiaratoIl).toLocaleDateString("it-IT")}
                  {d.gestionaleCents != null
                    ? ` (Guesty ne conosce ${formatEur(d.gestionaleCents)})`
                    : ""}
                  .
                </li>
              ))}
            </ul>
          </li>
        ) : null}
        {dati.mesiRettificati.length > 0 ? (
          <li>
            Mesi chiusi e poi corretti. Il numero nel documento è quello
            corretto; quello di prima è conservato:
            <ul className="mt-1 flex flex-col gap-0.5 pl-4">
              {dati.mesiRettificati.map((r) => (
                <li key={`${r.nickname}-${r.mese}`}>
                  {r.nickname}, {MESI[Number(r.mese.slice(5, 7)) - 1]}:
                  rettificato il{" "}
                  {new Date(r.rettificatoIl).toLocaleDateString("it-IT")}
                  {r.motivo ? ` — ${r.motivo}` : ""}.
                </li>
              ))}
            </ul>
          </li>
        ) : null}
        {dati.mesiCambiati.length > 0 ? (
          <li>
            Su questi mesi il conto del documento è quello chiuso a suo tempo;
            il gestionale oggi ne dice un altro:
            <ul className="mt-1 flex flex-col gap-0.5 pl-4">
              {dati.mesiCambiati.map((c) => (
                <li key={`${c.nickname}-${c.mese}`}>
                  {c.nickname}, {MESI[Number(c.mese.slice(5, 7)) - 1]}:{" "}
                  <strong>{formatEur(c.primaCents)}</strong> nel documento,{" "}
                  {formatEur(c.oggiCents)} oggi.
                </li>
              ))}
            </ul>
          </li>
        ) : null}
      </ul>
    </section>
  );
}

/**
 * Il totale dell'anno, in una tabella che **quadra da sola**.
 *
 * Le righe stanno in quest'ordine per una ragione: partono da quello che
 * hanno pagato gli ospiti e arrivano all'utile togliendo una cosa per volta,
 * così ogni riga si verifica con una sottrazione.
 *
 *     venduto lordo − trattenute = incassato
 *     incassato − fatturate − imposta − altri costi = utile
 *
 * Prima c'era una riga sola, «Commissioni dei canali», che valeva le
 * fatturate più IVA e costi di incasso e **non** le trattenute alla fonte:
 * un numero che non corrispondeva a nessuna delle due colonne del quadro per
 * canale. Un commercialista che trova una differenza che non sa spiegare
 * smette di fidarsi di tutto il foglio, e fa bene.
 */
function Riepilogo({ dati }: { dati: Dati }) {
  const t = dati.totali;
  const vendutoLordo = somma(dati.canali, (c) => c.vendutoLordoCents);
  const trattenute = somma(dati.canali, (c) => c.commissioniTrattenuteCents);

  const voci: Array<[string, string, string?]> = [
    [
      "Venduto lordo",
      formatEur(vendutoLordo),
      "quello che hanno pagato gli ospiti",
    ],
    [
      "Commissioni trattenute dai canali",
      `− ${formatEur(trattenute)}`,
      "tolte prima del versamento: non le paghi, ma sono un tuo costo",
    ],
    [
      "Incassato",
      formatEur(t.ricaviLordiCents),
      "quello che i canali hanno versato sul conto",
    ],
    [
      "Commissioni e costi fatturati a te",
      `− ${formatEur(t.commissioniCents)}`,
      "commissioni a fattura, IVA sulle commissioni, costi di incasso",
    ],
    [
      "Imposta di soggiorno a tuo carico",
      `− ${formatEur(t.impostaSoggiornoCents)}`,
      "incassata da te e da versare al Comune",
    ],
    ["Altri costi", `− ${formatEur(t.altriCostiCents)}`],
    ["Utile", formatEur(t.utileCasaCents)],
    ["Notti vendute", String(t.nottiVendute)],
  ];

  return (
    <Tabella>
      <tbody>
        {voci.map(([etichetta, valore, spiega]) => (
          <tr key={etichetta}>
            <Td>
              {etichetta}
              {spiega ? (
                <span className="block text-[11px] leading-snug text-ink-faint">
                  {spiega}
                </span>
              ) : null}
            </Td>
            <Td n>
              <strong>{valore}</strong>
            </Td>
          </tr>
        ))}
      </tbody>
    </Tabella>
  );
}

/** Somma una colonna. In centesimi interi, come tutto il resto. */
function somma<T>(righe: readonly T[], di: (r: T) => number): number {
  return righe.reduce((t, r) => t + di(r), 0);
}

/**
 * I dodici mesi di **tutto** il portafoglio.
 *
 * Prima ogni immobile aveva la sua tabella da tredici righe: con quindici case
 * erano quindici tabelle, con cento sarebbero cento. Il commercialista non
 * legge trentatré pagine — e infatti non le leggeva nessuno. Qui i mesi sono
 * dodici in tutto, e il dettaglio per casa sta in una riga ciascuna.
 */
/** Una riga della tabella dei mesi, con dentro anche il conto di oggi. */
type RigaMese = MeseDelFascicolo & {
  discordante: boolean;
  rettificato: boolean;
  /** Almeno una casa, in quel mese, ha i numeri inseriti dall'host. */
  dichiarato: boolean;
};

function mesiDelPortafoglio(dati: Dati): RigaMese[] {
  const per = new Map<string, RigaMese>();
  for (const immobile of dati.immobili) {
    for (const m of immobile.mesi) {
      const riga: RigaMese = per.get(m.mese) ?? {
        ...m,
        ricaviOggiCents: null,
        discordante: false,
        rettificato: false,
        dichiarato: false,
        nottiVendute: 0,
        nottiDisponibili: 0,
        ricaviLordiCents: 0,
        commissioniCents: 0,
        impostaSoggiornoCents: 0,
        altriCostiCents: 0,
        utileCasaCents: 0,
        stimatoCents: 0,
      };
      riga.nottiVendute += m.nottiVendute;
      riga.nottiDisponibili += m.nottiDisponibili;
      riga.ricaviLordiCents += m.ricaviLordiCents;
      riga.commissioniCents += m.commissioniCents;
      riga.impostaSoggiornoCents += m.impostaSoggiornoCents;
      riga.altriCostiCents += m.altriCostiCents;
      riga.utileCasaCents += m.utileCasaCents;
      riga.stimatoCents += m.stimatoCents;
      // Quello che il gestionale dice **oggi** per lo stesso mese. Si somma
      // sempre, anche quando coincide: se anche un solo immobile discorda, la
      // riga deve poter mostrare il totale di oggi accanto a quello del
      // documento — non il totale di uno solo.
      riga.ricaviOggiCents =
        (riga.ricaviOggiCents ?? 0) + (m.ricaviOggiCents ?? m.ricaviLordiCents);
      if (m.ricaviOggiCents != null) riga.discordante = true;
      if (m.rettificatoIl) riga.rettificato = true;
      if (m.dichiaratoIl) riga.dichiarato = true;
      per.set(m.mese, riga);
    }
  }
  return [...per.values()].sort((a, b) => a.mese.localeCompare(b.mese));
}

function Tabella({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[12.5px]">{children}</table>
    </div>
  );
}

function Th({ children, n }: { children?: React.ReactNode; n?: boolean }) {
  return (
    <th
      className={`border-b border-line py-1.5 text-[11.5px] font-bold uppercase tracking-wide text-ink-faint ${n ? "text-right" : "text-left"}`}
    >
      {children}
    </th>
  );
}

function Td({ children, n }: { children?: React.ReactNode; n?: boolean }) {
  return (
    <td
      className={`border-b border-line py-1.5 text-ink ${n ? "text-right tabular-nums" : "text-left"}`}
    >
      {children}
    </td>
  );
}

/** La scheda in Impostazioni che lo apre. */
export function AperturaFascicolo({
  onApri,
}: {
  onApri: (anno: number) => void;
}) {
  const anni = anniFascicolo();

  return (
    <Card className="p-4">
      <div className="flex items-start gap-2.5">
        <Printer size={17} className="mt-0.5 shrink-0 text-accent" />
        <div>
          <p className="text-[15px] font-bold text-ink">
            Fascicolo per il commercialista
          </p>
          <p className="mt-0.5 text-[13px] leading-relaxed text-ink-soft">
            Tutto un anno in un foglio: totali, dodici mesi per immobile e
            l&apos;elenco delle prenotazioni. I mesi chiusi vengono dal conto
            congelato a suo tempo, quindi non cambiano più.
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2.5">
        {anni.map((anno) => (
          <button
            key={anno}
            type="button"
            onClick={() => onApri(anno)}
            aria-label={String(anno)}
            className="group flex items-center gap-3 rounded-card border border-line px-3.5 py-3 text-left transition-all duration-quick hover:border-line-strong hover:bg-surface-muted active:scale-[0.98]"
          >
            <span className="grid size-9 shrink-0 place-items-center rounded-field bg-surface-muted text-ink transition-colors duration-quick group-hover:bg-surface">
              <FileText size={16} />
            </span>
            <span>
              <span className="block text-[15px] font-extrabold text-ink">
                {anno}
              </span>
              <span className="block text-[12px] text-ink-soft">
                {anno === new Date().getFullYear()
                  ? "anno in corso"
                  : "anno chiuso"}
              </span>
            </span>
          </button>
        ))}
      </div>
    </Card>
  );
}

/** Gli anni che si possono aprire: quello passato e quello in corso. */
export function anniFascicolo(): number[] {
  const corrente = new Date().getFullYear();
  return [corrente - 1, corrente];
}
